/**
 * React Query client configuration with persistent caching
 */

import { QueryClient } from '@tanstack/react-query';
import { persistentCache } from './persistentCache';
import { createLogger } from './logger';

/**
 * Get user-specific cache key
 * This ensures each user has their own cache
 */
const getCacheKey = () => {
  try {
    // Try to get userId from localStorage (set by Clerk)
    const clerkData = localStorage.getItem('clerk-db-jwt');
    if (clerkData) {
      const parsed = JSON.parse(clerkData);
      const userId = parsed?.userId;
      if (userId) {
        return `react-query-cache-${userId}`;
      }
    }
  } catch (error) {
    // If we can't get userId, use default key
    console.warn('Could not get userId for cache key', error);
  }

  // Fallback to a generic key for guests
  return 'react-query-cache-guest';
};

/**
 * Custom persister using our IndexedDB cache
 * Compatible with @tanstack/react-query-persist-client
 *
 * Each user gets their own cache to prevent data leakage
 */
export const persister = {
  persistClient: async (client: any) => {
    const cacheKey = getCacheKey();
    await persistentCache.set(cacheKey, client, 24 * 60 * 60 * 1000); // 24 hours
  },
  restoreClient: async () => {
    const cacheKey = getCacheKey();
    const cached = await persistentCache.get<any>(cacheKey);
    return cached ?? undefined;
  },
  removeClient: async () => {
    const cacheKey = getCacheKey();
    await persistentCache.delete(cacheKey);
  },
};

/**
 * Create React Query client with optimized defaults
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Caching strategy
      staleTime: 5 * 60 * 1000, // 5 minutes - data is considered fresh
      gcTime: 30 * 60 * 1000, // 30 minutes - garbage collection time (formerly cacheTime)

      // Retry strategy
      retry: 2,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),

      // Refetch strategy
      refetchOnWindowFocus: false, // Don't refetch on window focus (can be annoying for users)
      refetchOnReconnect: true, // Refetch when network reconnects
      refetchOnMount: false, // Don't refetch if data is fresh

      // Network mode
      networkMode: 'online', // Only run queries when online
    },
    mutations: {
      // Retry mutations once on failure
      retry: 1,
      networkMode: 'online',
    },
  },
});

/**
 * Query keys factory for type-safe and consistent query keys
 *
 * IMPORTANT: User-specific data (chats, search history) should include userId
 * in the query key to prevent data leakage between users.
 */
export const queryKeys = {
  // Weather queries (public data, no userId needed)
  weather: {
    all: ['weather'] as const,
    byLocation: (location: string) => ['weather', location] as const,
  },

  // Destination queries (public data, no userId needed)
  destination: {
    all: ['destination'] as const,
    info: (destination: string) => ['destination', 'info', destination] as const,
  },

  // Activity queries (public data, no userId needed)
  activity: {
    all: ['activity'] as const,
    details: (activityName: string, destinationName: string, locationName?: string) =>
      ['activity', 'details', destinationName, activityName, locationName] as const,
    photos: (activityName: string, destinationName: string) =>
      ['activity', 'photos', destinationName, activityName] as const,
  },

  // State management
  states: {
    all: ['states'] as const,
    available: () => ['states', 'available'] as const,
    byFilename: (filename: string) => ['states', filename] as const,
  },

  // Travel plan (streaming, not cached in React Query)
  travelPlan: {
    all: ['travelPlan'] as const,
  },

  // User-specific queries (include userId to prevent cross-user data leakage)
  chats: {
    all: (userId: string) => ['chats', userId] as const,
    list: (userId: string) => ['chats', userId, 'list'] as const,
    detail: (userId: string, chatId: string) => ['chats', userId, 'detail', chatId] as const,
  },

  searchHistory: {
    all: (userId: string) => ['searchHistory', userId] as const,
    list: (userId: string) => ['searchHistory', userId, 'list'] as const,
    detail: (userId: string, searchId: string) => ['searchHistory', userId, 'detail', searchId] as const,
  },
} as const;

/**
 * Invalidate all queries
 */
export const invalidateAllQueries = async () => {
  await queryClient.invalidateQueries();
  log.info('Invalidated all React Query caches');
};

/**
 * Clear all query caches
 */
export const clearAllQueries = async () => {
  queryClient.clear();
  await persister.removeClient();
  log.info('Cleared all React Query caches');
};

/**
 * Prefetch query helper
 */
export const prefetchQuery = async <T,>(
  queryKey: readonly unknown[],
  queryFn: () => Promise<T>
) => {
  await queryClient.prefetchQuery({
    queryKey,
    queryFn,
  });
};

/**
 * Get query cache stats
 */
export const getQueryCacheStats = () => {
  const cache = queryClient.getQueryCache();
  const queries = cache.getAll();

  return {
    totalQueries: queries.length,
    activeQueries: queries.filter(q => q.getObserversCount() > 0).length,
    staleQueries: queries.filter(q => q.isStale()).length,
    fetchingQueries: queries.filter(q => q.state.fetchStatus === 'fetching').length,
  };
};

export default queryClient;
const log = createLogger('queryClient');
